import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import { PlayArrowOutlined } from "@mui/icons-material";
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Chip,
  FormControl,
  FormControlLabel,
  InputLabel,
  Link,
  MenuItem,
  Select,
  SelectChangeEvent,
  Switch,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  DataGridPro,
  GridCellParams,
  GridColDef,
  GridRenderCellParams,
  GridRowParams,
} from "@mui/x-data-grid-pro";
import SearchBox from "../../../components/form/SearchBox/SearchBox";
import WorkflowCanvasIcon from "../../../features/workflows/WorkflowCanvasIcon";
import WorkflowMyWorkPanel from "../../../features/workflows/WorkflowMyWorkPanel";
import {
  WorkflowInstanceListItem,
  WorkflowInstanceStatus,
  WorkflowMyWorkItem,
  WORKFLOW_INSTANCE_STATUS_LABELS,
} from "../../../features/workflows/workflowInstancesData";
import { PIF_DEFINITION_TOKEN } from "../../../features/workflows/pifWorkflowKeys";
import useWorkflowInstancesList from "../../../hooks/useWorkflowInstancesList";
import useWorkflowMyWork from "../../../hooks/useWorkflowMyWork";
import useWorkflowInstanceFilterDefinitions from "../../../hooks/useWorkflowInstanceFilterDefinitions";
import { useMenu } from "../../../context/MenuContext";
import {
  dataGridHeaderHeight,
  dataGridRowHeight,
  ModuleNames,
} from "../../../data/constants";
import PageBand from "../../../layouts/page-detail/PageBand";
import "./WorkflowInstances.scss";

const BREADCRUMB_LINK_COLOR = "rgba(255, 255, 255, 0.75)";
const DEFAULT_PAGE_SIZE = 25;
const MY_WORK_PATH = "/workflow/my-work";

/** Opens a case on the Run screen; the stage is passed when there is one to land on. */
// `source` says which list the case was opened from, so the Run screen's navigation trace names that
// module (EnterprisePortal-Workflow-Instances or -MyWork); both bring the person back here.
const runUrl = (instanceId: number, stageId: string | null, source: "instances" | "mywork"): string => {
  const params = new URLSearchParams({ instanceId: String(instanceId) });
  if (stageId) params.set("stageId", stageId);
  params.set("source", source);
  return `/workflow/run?${params.toString()}`;
};

const WorkflowInstances: React.FC = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { permissions } = useMenu();

  // Epic 80431: each panel shows only if the user holds that module's permission; the screen opens
  // with either (MenuGuard).
  const canView = useCallback(
    (moduleName: string) =>
      Boolean(permissions?.find((permission) => permission.moduleName === moduleName)?.canView),
    [permissions],
  );
  const canViewInstances = canView(ModuleNames.WorkflowInstances);
  const canViewMyWork = canView(ModuleNames.WorkflowMyWork);
  // Starting a case is My Work CanEdit - the same grant MenuGuard checks for /workflow/run?token=.
  const canStartCase = Boolean(
    permissions?.find((permission) => permission.moduleName === ModuleNames.WorkflowMyWork)?.canEdit,
  );

  const [search, setSearch] = useState("");
  const [definitionRootId, setDefinitionRootId] = useState<number | "all">("all");
  const [status, setStatus] = useState<WorkflowInstanceStatus | "all">("all");
  // Epic 80431: other people's Builder test runs - off by default for everyone, Admin and HR included.
  const [includeTest, setIncludeTest] = useState(false);

  const filters = useMemo(
    () => ({ search, definitionRootId, status, includeTest }),
    [search, definitionRootId, status, includeTest],
  );
  const instances = useWorkflowInstancesList(filters, DEFAULT_PAGE_SIZE, canViewInstances);
  const definitions = useWorkflowInstanceFilterDefinitions(canViewInstances);
  const myWork = useWorkflowMyWork(canViewMyWork);

  // /workflow/my-work is the same screen with the inbox in focus.
  const myWorkRef = useRef<HTMLElement>(null);
  useEffect(() => {
    if (pathname.toLowerCase() === MY_WORK_PATH && canViewMyWork) {
      myWorkRef.current?.focus();
      myWorkRef.current?.scrollIntoView?.({ block: "start" });
    }
  }, [pathname, canViewMyWork]);

  const openInstance = useCallback(
    (instance: WorkflowInstanceListItem) => navigate(runUrl(instance.id, instance.currentStageId, "instances")),
    [navigate],
  );

  const openWorkItem = useCallback(
    (item: WorkflowMyWorkItem) => navigate(runUrl(item.instanceId, item.stageId, "mywork")),
    [navigate],
  );

  const handleSearch = useCallback((value: string) => setSearch(value), []);

  const handleWorkflowChange = (event: SelectChangeEvent<number | "all">) =>
    setDefinitionRootId(event.target.value === "all" ? "all" : Number(event.target.value));

  const handleStatusChange = (event: SelectChangeEvent<WorkflowInstanceStatus | "all">) =>
    setStatus(event.target.value as WorkflowInstanceStatus | "all");

  const handleRowClick = (params: GridRowParams<WorkflowInstanceListItem>) => openInstance(params.row);

  const handleRowKeyDown = (params: GridCellParams<WorkflowInstanceListItem>, event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      event.preventDefault();
      openInstance(params.row);
    }
  };

  const columns = useMemo<GridColDef<WorkflowInstanceListItem>[]>(
    () => [
      {
        field: "title",
        headerName: "Case",
        minWidth: 190,
        flex: 1.6,
        sortable: false,
        // Same rule as the folder list's Name and Actions columns: one column stays visible so
        // the column menu can never hide every column and leave no way back.
        hideable: false,
        renderCell: ({ row }: GridRenderCellParams<WorkflowInstanceListItem>) => {
          const reference = [row.refNo, row.subtitle].filter(Boolean).join(" · ");
          return (
            <Box className="workflow-instances__case-cell">
              {/* The Test badge sits on the title line, so the cell keeps its two-line height. */}
              <Box className="workflow-instances__case-title-row">
                <Tooltip title={row.title} placement="bottom-start">
                  <Typography component="span" className="workflow-instances__case-title">
                    {row.title}
                  </Typography>
                </Tooltip>
                {row.isTest && (
                  <Chip size="small" variant="outlined" color="secondary" label="Test" className="workflow-instances__test-chip" />
                )}
              </Box>
              <Tooltip title={reference} placement="bottom-start">
                <Typography component="span" className="workflow-instances__case-reference">
                  {reference}
                </Typography>
              </Tooltip>
            </Box>
          );
        },
      },
      {
        field: "currentStageName",
        headerName: "Current stage",
        minWidth: 130,
        flex: 1,
        sortable: false,
        renderCell: ({ row }: GridRenderCellParams<WorkflowInstanceListItem>) => (
          <Box className="workflow-instances__stage-cell">
            {row.currentStageType && <WorkflowCanvasIcon type={row.currentStageType} />}
            <Tooltip title={row.currentStageName} placement="bottom-start">
              <Typography component="span" className="workflow-instances__ellipsis">
                {row.currentStageName}
              </Typography>
            </Tooltip>
          </Box>
        ),
      },
      {
        field: "status",
        headerName: "Status",
        width: 112,
        sortable: false,
        renderCell: ({ row }: GridRenderCellParams<WorkflowInstanceListItem>) => (
          <Chip
            size="small"
            label={WORKFLOW_INSTANCE_STATUS_LABELS[row.status]}
            className={`workflow-instances__status workflow-instances__status--${row.status}`}
          />
        ),
      },
      {
        field: "owner",
        headerName: "Owner",
        minWidth: 96,
        flex: 0.6,
        sortable: false,
        renderCell: ({ row }: GridRenderCellParams<WorkflowInstanceListItem>) => (
          <Tooltip title={row.owner} placement="bottom-start">
            <Typography component="span" className="workflow-instances__ellipsis">
              {row.owner}
            </Typography>
          </Tooltip>
        ),
      },
      { field: "age", headerName: "Age", width: 60, sortable: false },
    ],
    [],
  );

  return (
    <main className="workflow-instances">
      <PageBand
        title="Instances & My Work"
        breadcrumbs={
          <Breadcrumbs
            aria-label="breadcrumbs"
            sx={{ "& .MuiBreadcrumbs-separator": { color: BREADCRUMB_LINK_COLOR } }}
          >
            <Link
              component={RouterLink}
              to="/workflow/library"
              underline="hover"
              sx={{ color: BREADCRUMB_LINK_COLOR }}
            >
              Workflow Management
            </Link>
            <Typography sx={{ fontWeight: 600, color: "#fff" }}>Instances & My Work</Typography>
          </Breadcrumbs>
        }
        actions={
          canStartCase && (
            <Button
              variant="contained"
              startIcon={<PlayArrowOutlined />}
              // Epic 80430: by Token, the PIF's latest published version — readable with My Work access
              // alone, so a Hiring Supervisor can start one without Library access.
              onClick={() => navigate(`/workflow/run?token=${encodeURIComponent(PIF_DEFINITION_TOKEN)}`)}
            >
              Run new PIF
            </Button>
          )
        }
      />

      <Box className="workflow-instances__layout">
        {canViewInstances && (
          <section
            className="workflow-instances__panel workflow-instances__instances"
            aria-labelledby="workflow-instances-heading"
          >
            <Box className="workflow-instances__panel-heading">
              <Typography component="h2" id="workflow-instances-heading">
                Instances
              </Typography>
            </Box>

            <Box className="workflow-instances__toolbar">
              <Box className="search-box-wrap workflow-instances__search">
                <SearchBox searchPlaceholder="Search instances…" onSearch={handleSearch} />
              </Box>
              <FormControl className="workflow-instances__filter" size="small">
                <InputLabel id="instances-workflow-label">Workflow</InputLabel>
                <Select
                  labelId="instances-workflow-label"
                  label="Workflow"
                  value={definitionRootId}
                  onChange={handleWorkflowChange}
                >
                  <MenuItem value="all">All workflows</MenuItem>
                  {definitions.map((definition) => (
                    <MenuItem key={definition.rootDefinitionId} value={definition.rootDefinitionId}>
                      {definition.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl className="workflow-instances__filter" size="small">
                <InputLabel id="instances-status-label">Status</InputLabel>
                <Select
                  labelId="instances-status-label"
                  label="Status"
                  value={status}
                  onChange={handleStatusChange}
                >
                  <MenuItem value="all">All statuses</MenuItem>
                  {Object.entries(WORKFLOW_INSTANCE_STATUS_LABELS).map(([value, label]) => (
                    <MenuItem key={value} value={value}>
                      {label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControlLabel
                className="workflow-instances__include-test"
                control={<Switch size="small" checked={includeTest} onChange={(_, checked) => setIncludeTest(checked)} />}
                label="Include test runs"
              />
            </Box>

            {instances.forbidden ? (
              // No rows are kept or shown: a 403 must not leak data loaded under an earlier context.
              <Alert severity="warning">You don&apos;t have access to instances.</Alert>
            ) : (
              <>
                {instances.error && (
                  // The last good page stays below - a transient failure must not blank the roster.
                  <Alert
                    severity="error"
                    action={
                      <Button color="inherit" size="small" onClick={instances.retry}>
                        Retry
                      </Button>
                    }
                  >
                    Couldn&apos;t load instances, retry.
                  </Alert>
                )}
                <Box className="workflow-instances__grid-wrap">
                  <DataGridPro
                    className="workflow-instances__grid"
                    rows={instances.rows}
                    columns={columns}
                    rowCount={instances.total}
                    loading={instances.loading}
                    paginationMode="server"
                    rowHeight={dataGridRowHeight}
                    columnHeaderHeight={dataGridHeaderHeight}
                    hideFooterPagination
                    hideFooterSelectedRowCount
                    onRowsScrollEnd={instances.loadMore}
                    disableRowSelectionOnClick
                    onRowClick={handleRowClick}
                    onCellKeyDown={handleRowKeyDown}
                    getRowClassName={() => "workflow-instances__row"}
                    slots={{
                      noRowsOverlay: () => <Box className="workflow-instances__empty">No instances match.</Box>,
                    }}
                  />
                </Box>
              </>
            )}
          </section>
        )}

        {canViewMyWork && (
          <WorkflowMyWorkPanel
            ref={myWorkRef}
            items={myWork.items}
            viewerRole={myWork.viewerRole}
            loading={myWork.loading}
            error={myWork.error}
            onRetry={myWork.retry}
            onOpen={openWorkItem}
          />
        )}
      </Box>
    </main>
  );
};

export default WorkflowInstances;
